import { useSyncExternalStore } from 'react';
import {
  Megaphone, MessageCircle, HelpCircle, BookOpen,
  Bell, FileText, Code, Info,
  List, Pencil,
  Lock, MessageSquare, FolderOpen,
} from '../../icons';
import { SidebarGroup, type SidebarSubItem } from '../../components/SidebarGroup';
import { tBoard, getBoardLang, subscribeBoardLang } from '../../lib/board-i18n';

/**
 * BoardSidebar — 시안 InfoView (init-docs/WorkChat.jsx line 2940~2961) 의 4 group sidebar.
 *
 * 매핑 (mockup id → 현재 boardApi):
 *   - notice_all       → listNotices(false)
 *   - notice_pinned    → listNotices(true)        (= importantOnly)
 *   - board_all        → listPosts(undefined)
 *   - board_free       → listPosts('free')
 *   - board_tech       → listPosts('tech')
 *   - board_info       → listPosts('info')
 *   - inquiry_list     → listInquiries() (creating=false)
 *   - inquiry_write    → listInquiries() + creating=true
 *   - faq_all          → listFaqs(undefined, q)
 *   - faq_account      → listFaqs('계정/보안', q)
 *   - faq_chat         → listFaqs('채팅/알림', q)
 *   - faq_file         → listFaqs('파일/연동', q)
 *
 * 12 sub-item 전부 active (backend 4 도메인 모두 구현). FAQ 카테고리 라벨이 backend 에
 * 미등록이면 빈 결과 — UI 차단 안 함.
 */

export type BoardSidebarItemId =
  | 'notice_all' | 'notice_pinned'
  | 'board_all' | 'board_free' | 'board_tech' | 'board_info'
  | 'inquiry_list' | 'inquiry_write'
  | 'faq_all' | 'faq_account' | 'faq_chat' | 'faq_file';

interface BoardSidebarProps {
  activeId: BoardSidebarItemId;
  onSelect: (id: BoardSidebarItemId) => void;
  pinnedCount?: number;
}

interface BoardGroupDef {
  key: string;
  label: string;
  icon: typeof Megaphone;
  items: ReadonlyArray<SidebarSubItem & { id: BoardSidebarItemId }>;
}

export function BoardSidebar({ activeId, onSelect, pinnedCount }: BoardSidebarProps) {
  useSyncExternalStore(subscribeBoardLang, getBoardLang, getBoardLang);
  const groups: ReadonlyArray<BoardGroupDef> = [
    {
      key: 'notice',
      label: tBoard('board.group.notice'),
      icon: Megaphone,
      items: [
        { id: 'notice_all', label: tBoard('board.item.notice_all'), icon: Megaphone },
        { id: 'notice_pinned', label: tBoard('board.item.notice_pinned'), icon: Bell, badge: pinnedCount },
      ],
    },
    {
      key: 'board',
      label: tBoard('board.group.posts'),
      icon: MessageCircle,
      items: [
        { id: 'board_all', label: tBoard('board.item.posts_all'), icon: FileText },
        { id: 'board_free', label: tBoard('board.item.posts_free'), icon: MessageCircle },
        { id: 'board_tech', label: tBoard('board.item.posts_tech'), icon: Code },
        { id: 'board_info', label: tBoard('board.item.posts_info'), icon: Info },
      ],
    },
    {
      key: 'inquiry',
      label: tBoard('board.group.inquiry'),
      icon: HelpCircle,
      items: [
        { id: 'inquiry_list', label: tBoard('board.item.inquiry_list'), icon: List },
        { id: 'inquiry_write', label: tBoard('board.item.inquiry_write'), icon: Pencil },
      ],
    },
    {
      key: 'faq',
      label: tBoard('board.group.faq'),
      icon: BookOpen,
      items: [
        { id: 'faq_all', label: tBoard('board.item.faq_all'), icon: BookOpen },
        { id: 'faq_account', label: tBoard('board.item.faq_account'), icon: Lock },
        { id: 'faq_chat', label: tBoard('board.item.faq_chat'), icon: MessageSquare },
        { id: 'faq_file', label: tBoard('board.item.faq_file'), icon: FolderOpen },
      ],
    },
  ];

  return (
    <nav className="board-sidebar" aria-label={tBoard('board.nav_aria')}>
      {groups.map((g) => (
        <SidebarGroup
          key={g.key}
          groupKey={g.key}
          label={g.label}
          icon={g.icon}
          items={g.items}
          activeId={activeId}
          onSelect={(id) => onSelect(id as BoardSidebarItemId)}
          defaultExpanded
        />
      ))}
    </nav>
  );
}
